import { Observable } from "rxjs";

export type FingerprintDeviceId = "STARTEK" | "ARATEK" | "MANTRA";
export type FingerprintDevicePreference = FingerprintDeviceId | "AUTO";

/** A connected reader, ready to capture. */
export interface ConnectedReader {
  device: FingerprintDevice;
  /** Sent to the backend as `device_name`; it picks the image decoder from this. */
  model: string;
  /** Replaces the device's own "service not available" error, e.g. when Auto found no reader. */
  unavailableError?: FingerprintDeviceError;
}

/**
 * `not-found`: the reader's service didn't answer; Auto mode moves on to the next reader.
 * `no-device`: the service answered but no reader is connected (or another program holds it);
 *   Auto mode moves on too, and the user gets a popup telling them to connect the reader.
 * `unavailable`: the service stopped answering during capture; the user is told to check it.
 * `capture`: the reader answered but the capture failed; shown inline so the user can try again.
 */
export type FingerprintDeviceErrorKind = "not-found" | "no-device" | "unavailable" | "capture";

export class FingerprintDeviceError extends Error {
  constructor(
    public readonly kind: FingerprintDeviceErrorKind,
    message: string,
    public readonly code?: number
  ) {
    super(message);
  }
}

export interface FingerprintDevice {
  readonly id: FingerprintDeviceId;
  readonly label: string;
  /** Emits the connected reader, or errors with a FingerprintDeviceError. */
  connect(): Observable<ConnectedReader>;
  /** Emits the captured image bytes (WSQ or BMP, depending on the reader). */
  capture(reader: ConnectedReader): Observable<number[]>;
}

export function base64ToBytes(base64: string): number[] {
  return Array.from(Uint8Array.from(atob(base64), (c) => c.charCodeAt(0)));
}
